import { useState, useRef, useEffect } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import ErrorBoundary from './ErrorBoundary'

const navItems = [
  { to: '/dashboard', label: 'Ideas' },
  { to: '/chat', label: 'Chat' },
  { to: '/todos', label: 'TODOs' },
  { to: '/canon', label: 'Canon' },
]

const adminItems = [
  { to: '/admin', label: 'Admin' },
  { to: '/admin/agent', label: 'Agent' },
  { to: '/admin/analytics', label: 'Analytics' },
]

export default function Layout() {
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menus on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Close mobile nav on route change
  const closeMobileNav = () => setMobileNavOpen(false)

  const handleSignOut = async () => {
    setMenuOpen(false)
    await signOut()
    navigate('/login')
  }

  return (
    // Use 100dvh so layout shrinks when virtual keyboard appears (PWA standalone)
    <div className="h-[100dvh] flex flex-col overflow-hidden bg-gray-950 dark:bg-gray-950">
      <nav className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2 mr-6"
            onClick={closeMobileNav}
          >
            <span className="text-lg font-bold text-brand-400">ReThinker</span>
          </NavLink>

          {/* Desktop nav — hidden on small screens */}
          <div className="hidden sm:flex gap-1 flex-1">
            {navItems.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/dashboard'}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-600 text-white'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}

            {/* Admin nav - only for admin users */}
            {user?.role === 'admin' && (
              <>
                <div className="w-px bg-gray-700 mx-2" />
                {adminItems.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end
                    className={({ isActive }) =>
                      `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-purple-600 text-white'
                          : 'text-purple-400 hover:text-purple-200 hover:bg-gray-800'
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </>
            )}
          </div>

          {/* Right side: hamburger (mobile) + theme toggle + user menu */}
          <div className="flex items-center gap-2 ml-auto sm:ml-0">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileNavOpen(o => !o)}
              className="sm:hidden p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-md transition-colors"
              aria-label="Toggle navigation"
            >
              {mobileNavOpen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-md transition-colors"
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {/* User menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 hover:text-gray-100 hover:bg-gray-800 rounded-md transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-brand-600 flex items-center justify-center text-xs font-bold text-white">
                  {user?.email?.charAt(0).toUpperCase() || '?'}
                </div>
                <span className="hidden sm:inline max-w-[120px] truncate">
                  {user?.email || 'User'}
                </span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 py-1">
                  <div className="px-4 py-2 border-b border-gray-700">
                    <p className="text-sm text-gray-200 truncate">{user?.email}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role || 'user'}</p>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/profile') }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/settings') }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                  >
                    Settings
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile nav drawer — slides open below the navbar */}
        {mobileNavOpen && (
          <div className="sm:hidden border-t border-gray-800 py-2">
            {[...navItems, ...(user?.role === 'admin' ? adminItems : [])].map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/dashboard'}
                onClick={closeMobileNav}
                className={({ isActive }) =>
                  `block px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-white bg-brand-600/20 border-l-2 border-brand-500'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        )}
      </nav>
      <main className="flex-1 min-h-0 flex flex-col">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  )
}
